// project imports
import {
  Button,
  Grid,
  IconButton,
  Stack,
  Modal,
  Typography,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { IconPlus } from '@tabler/icons';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box } from '@mui/system';
import toast from 'react-hot-toast';
import { del, get, post } from '../../axios/apiService';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import SearchIcon from '@mui/icons-material/Search';
import { Modules } from 'store/constant';
import dayjs from 'dayjs';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #11111111',
  boxShadow: 24,
  p: 4
};

const CouponManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [permissionMatrix, setPermissionMatrix] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [open, setOpen] = useState(false);
  const [isDeleteCouponId, setIsDeleteCouponId] = useState('');
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [couponId, setCouponId] = useState('');

  // Fetch coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      setLoading(true);
      try {
        const sortField = sortCouponField(sortModel.length ? sortModel[0].field : 'createdDate');
        const sortOrder = sortModel.length ? (sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending') : 'Descending';
        const sortQuery = `&$orderby=${sortField}&$orderbydirection=${sortOrder}`;
        const searchQuery =
          search != ''
            ? `&$filter=code~LIKE~${search} OR name~LIKE~${search} OR description~LIKE~${search} OR category.name~LIKE~${search} OR discountType.name~LIKE~${search} OR purchaseType.name~LIKE~${search} OR discountValue~LIKE~${search} OR minPurchaseAmount~LIKE~${search} OR maxDiscountAmount~LIKE~${search}`
            : '';

        let response = await get(
          `Coupon?$skip=${paginationModel.page * paginationModel.pageSize}&$top=${paginationModel.pageSize}${searchQuery}${sortQuery}`
        );

        if (response != null) {
          setCoupons(response.values);
          setRowCount(response.totalCount);
        }
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
  }, [paginationModel, sortModel, search]);

  // Load permissions
  useEffect(() => {
    const UserPermissions = JSON.parse(localStorage.getItem(`UserPermissions`));
    const permission = UserPermissions?.find((x) => x.moduleId === Modules.CouponManagement);
    if (permission) setPermissionMatrix(permission);
    else setPermissionMatrix(null);
  }, []);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Action buttons
  const ActionButtons = (row) => (
    <Stack direction={'row'} spacing={1}>
      <IconButton title="Notify via Email" color="primary" onClick={() => handleNotifyCoupons(row.id)}>
        <EmailIcon />
      </IconButton>
      <IconButton color="primary" onClick={() => navigate(`/coupon-management/edit-coupon/${row.id}`)}>
        <EditIcon />
      </IconButton>
      <IconButton
        color="error"
        onClick={() => {
          setOpen(true);
          setIsDeleteCouponId(row.id);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  );

  const handleClose = () => {
    setOpen(false);
    setIsDeleteCouponId('');
  };

  // Grid columns for coupon
  const columns = [
    { field: 'name', headerName: 'Coupon Name', flex: 1 },
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'categoryName', headerName: 'Category', flex: 1 },
    { field: 'discountTypeName', headerName: 'Discount Type', flex: 1 },
    { field: 'discountValue', headerName: 'Discount Value', flex: 1 },
    { field: 'minPurchaseAmount', headerName: 'Min Purchase', flex: 1 },
    { field: 'maxDiscountAmount', headerName: 'Max Discount', flex: 1 },
    {
      field: 'validFrom',
      headerName: 'Valid From',
      flex: 1,
      renderCell: (params) => (params.row.validFrom ? dayjs(params.row.validFrom).format('DD-MMM-YYYY') : '')
    },
    {
      field: 'validTo',
      headerName: 'Valid To',
      flex: 1,
      renderCell: (params) => (params.row.validTo ? dayjs(params.row.validTo).format('DD-MMM-YYYY') : '')
    },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params) => <ActionButtons {...params.row} />,
      align: 'right',
      width: 180,
      sortable: false,
      filterable: false
    }
  ];

  // const DeleteCoupon = async () => {
  //     try {
  //       if (isDeleteCouponId) {
  //         await del(`Coupon/${isDeleteCouponId}`);
  //         setOpen(false);
  //         await fetchRecurring();
  //       }
  //     } catch (error) {
  //       console.log(error.message);
  //     }
  //   };

  const handleNotifyCoupons = (id) => {
    setCouponId(id);
    setNotifyDialogOpen(true);
  };

  const confirmNotifyAllCoupons = async () => {
    setNotifyDialogOpen(false);
    setLoading(true);
    try {
      let response = await post(`Coupon/notify/` + couponId, null);
      if (!response) throw new Error('Failed to queue coupon notifications for processing.');
      toast.success('All coupon notifications have been queued for processing.');
    } catch (error) {
      console.error(error.message);
      toast.error('Failed to queue notifications.');
    } finally {
      setLoading(false);
    }
  };

  const DeleteCoupon = async () => {
    if (!isDeleteCouponId) return setOpen(false);
    setLoading(true);
    try {
      let response = await del(`Coupon/${isDeleteCouponId}`, null);
      if (!response) throw new Error(`HTTP error! status: ${response.status}`);
      toast.success('Coupon deleted successfully');
      handleClose();
      setPaginationModel({ page: 0, pageSize: 10 });
    } catch (error) {
      toast.error('Failed to delete coupon');
    } finally {
      setLoading(false);
    }
  };

  const handeRedirectAddCoupon = () => navigate('/coupon-management/add-coupon');

  return (
    <>
      <MainCard title="Coupon Management">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0}>
              <form noValidate autoComplete="off" style={{ marginRight: 10 }}>
                <FormControl sx={{ width: '25ch' }} size="small">
                  <OutlinedInput
                    placeholder="Search"
                    value={search}
                    onChange={handleSearchChange}
                    endAdornment={
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </form>
              {permissionMatrix && permissionMatrix.writeRights === true && (
                <>
                  <Button variant="contained" color="primary" onClick={handeRedirectAddCoupon} startIcon={<IconPlus />}>
                    Add
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <DataGrid
              getRowId={(row) => row.id}
              rows={coupons}
              rowCount={rowCount}
              columns={columns}
              paginationMode="server"
              sortingMode="server"
              pageSizeOptions={[10]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              components={{ NoRowsOverlay: CustomNoRowsOverlay }}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              columnVisibilityModel={{ action: permissionMatrix?.writeRights }}
            />
          </Grid>
        </Grid>
      </MainCard>

      {/* Delete Confirmation Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography sx={{ fontSize: '20px' }}>Delete Confirmation</Typography>
          <Typography sx={{ mt: 2 }}>Are you sure you want to delete this coupon?</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button onClick={handleClose} variant="contained" color="error" sx={{ mt: 2, mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" sx={{ mt: 2 }} onClick={DeleteCoupon}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={notifyDialogOpen}
        onClose={() => setNotifyDialogOpen(false)}
        aria-labelledby="notify-dialog-title"
        aria-describedby="notify-dialog-description"
      >
        <DialogTitle id="notify-dialog-title">Confirm Notification</DialogTitle>
        <DialogContent>
          <DialogContentText id="notify-dialog-description">
            Are you sure you want to queue notifications for all coupon users?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialogOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={confirmNotifyAllCoupons} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={isLoading} />
    </>
  );
};

export default CouponManagement;

// Sorting helper
const sortCouponField = (field) => {
  if (field === 'name') return 'Name';
  if (field === 'code') return 'Code';
  if (field === 'categoryName') return 'CategoryName';
  if (field === 'discountTypeName') return 'DiscountTypeName';
  return 'createdDate';
};
